"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { getBackgroundColorClass } from "@/utils/util"

type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  color?: string
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  color,
  ...props
}: CalendarProps) {
  const getAriaSelectedClasses = () => {
    if (!color) return 'aria-selected:bg-primary aria-selected:text-primary-foreground'

    switch (color) {
    case 'blue-500':
      return 'aria-selected:bg-blue-500 aria-selected:text-white'
    case 'red-500':
      return 'aria-selected:bg-red-500 aria-selected:text-white'
    case 'pink-500':
      return 'aria-selected:bg-pink-500 aria-selected:text-white'
    case 'purple-500':
      return 'aria-selected:bg-purple-500 aria-selected:text-white'
    case 'green-500':
      return 'aria-selected:bg-green-500 aria-selected:text-white'
    case 'amber-500':
      return 'aria-selected:bg-amber-500 aria-selected:text-white'
    default:
      return 'aria-selected:bg-blue-500 aria-selected:text-white'
    }
  }

  const getHoverFocusClasses = () => {
    if (!color) return 'hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground'

    switch (color) {
    case 'blue-500':
      return 'hover:bg-blue-500 hover:text-white focus:bg-blue-500 focus:text-white'
    case 'red-500':
      return 'hover:bg-red-500 hover:text-white focus:bg-red-500 focus:text-white'
    case 'pink-500':
      return 'hover:bg-pink-500 hover:text-white focus:bg-pink-500 focus:text-white'
    case 'purple-500':
      return 'hover:bg-purple-500 hover:text-white focus:bg-purple-500 focus:text-white'
    case 'green-500':
      return 'hover:bg-green-500 hover:text-white focus:bg-green-500 focus:text-white'
    case 'amber-500':
      return 'hover:bg-amber-500 hover:text-white focus:bg-amber-500 focus:text-white'
    default:
      return 'hover:bg-blue-500 hover:text-white focus:bg-blue-500 focus:text-white'
    }
  }
  const backgroundColorClass = getBackgroundColorClass(color)
  const ariaSelectedClasses = getAriaSelectedClasses()
  const hoverFocusClasses = getHoverFocusClasses()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        caption: "flex justify-center pt-1 relative items-center w-full",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-x-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "size-8 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: `day-range-start ${ariaSelectedClasses}`,
        day_range_end: `day-range-end ${ariaSelectedClasses}`,
        day_selected: `${backgroundColorClass} text-white ${hoverFocusClasses}`,
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("size-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("size-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  )
}

export { Calendar }
